/**
 * The pre-upload review of a chosen file, shared by every place a file is uploaded: a new
 * e-file, a correction, a replacement.
 *
 * The file is read, mapped, checked and totalled here in the browser (precheck.ts), and the
 * result is read one part at a time - how the columns were read, then how the recipient names
 * were identified (Resolve Names, as on the portal, only for a file with a combined name
 * column), then what is wrong (only when something is), then what the boxes add up to. The upload is offered on the last
 * part, once the person has said the totals are theirs. Which parts exist depends on the
 * file: a spreadsheet has a mapping, a 1220 file does not; a clean file has no issues part;
 * an IRIS transmission has no parts at all and uploads once it passes.
 */
import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, FileSpreadsheet, RefreshCw, UploadCloud, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Pill } from '@/components/ui/pill';
import { Spinner } from '@/components/ui/feedback';
import { Checkbox } from '@/components/ui/toggles';
import { formatBytes, formatCount } from '@/lib/format';
import { cn } from '@/lib/cn';
import { BoxTotals } from './box-totals';
import { ColumnMapping } from './check-report';
import { precheckFile, type PrecheckPhase, type PrecheckResult } from './precheck';
import { ResolveNames, useNameResolution } from './resolve-names';

/** The parts of the review, in the order they are read. */
export type ReviewPart = 'mapping' | 'names' | 'issues' | 'totals';

export const reviewPartCopy: Record<ReviewPart, { label: string }> = {
  mapping: { label: 'Column mapping' },
  names: { label: 'Resolve Names' },
  issues: { label: 'Issues' },
  totals: { label: 'Box totals' },
};

export const checkPhaseCopy: Record<PrecheckPhase, string> = {
  reading: 'Reading this file',
  parsing: 'Reading the records',
  checking: 'Checking the records',
};

export function partsOf(review: PrecheckResult | null, namesRequired = false): ReviewPart[] {
  const parts: ReviewPart[] = [];
  if (!review) return parts;
  if (review.mapping && Object.keys(review.mapping).length > 0) parts.push('mapping');
  if (namesRequired) parts.push('names');
  if (!review.ok) parts.push('issues');
  if (review.boxTotals && review.boxTotals.length > 0) parts.push('totals');
  return parts;
}

/**
 * Runs the check on a file whenever the file, the form or the year changes, and holds the
 * verdict. A verdict reached under a different year or form is not one to upload on, so
 * it is taken again when either moves.
 */
export function usePrecheck(
  file: File | null,
  options: { format: string | null; formType: string | null; taxYear: number; paused?: boolean },
) {
  const [passed, setPassed] = useState<PrecheckResult | null>(null);
  const [failed, setFailed] = useState<PrecheckResult | null>(null);
  const [phase, setPhase] = useState<PrecheckPhase | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { format, formType, taxYear, paused } = options;

  const checkedUnder = useRef<{ file: File; format: string | null; formType: string | null; taxYear: number } | null>(null);
  useEffect(() => {
    if (!file || !format || paused) return;
    const last = checkedUnder.current;
    const now = { file, format, formType, taxYear };
    if (last && last.file === file && last.format === format && last.formType === formType && last.taxYear === taxYear) {
      return;
    }
    checkedUnder.current = now;
    let cancelled = false;
    setPassed(null);
    setFailed(null);
    setError(null);
    void (async () => {
      try {
        const checked = await precheckFile(file, { format, formType, taxYear, onPhase: setPhase });
        if (cancelled) return;
        if (checked.ok) setPassed(checked);
        else setFailed(checked);
      } catch (caught) {
        if (!cancelled) setError(caught instanceof Error ? caught.message : String(caught));
      } finally {
        if (!cancelled) setPhase(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [file, format, formType, taxYear, paused]);

  useEffect(() => {
    if (file) return;
    checkedUnder.current = null;
    setPassed(null);
    setFailed(null);
    setPhase(null);
    setError(null);
  }, [file]);

  return { passed, failed, review: passed ?? failed, phase, error };
}

/** Where the person is in the review, and whether the upload may be offered yet. */
export function useReviewParts(file: File | null, review: PrecheckResult | null) {
  const names = useNameResolution(review);
  const parts = partsOf(review, names.plan.required);
  const [partIndex, setPartIndex] = useState(0);
  const [totalsConfirmed, setTotalsConfirmed] = useState(false);
  // A different file, or the same file checked again, starts the review over.
  useEffect(() => {
    setPartIndex(0);
    setTotalsConfirmed(false);
  }, [file, review]);
  const index = Math.min(partIndex, Math.max(parts.length - 1, 0));
  const part: ReviewPart | null = parts[index] ?? null;
  const onLastPart = parts.length === 0 || index >= parts.length - 1;
  const needsConfirmation = parts.includes('totals');
  /** Resolve Names holds the person until they accept the identifications, as on the portal. */
  const canAdvance = part !== 'names' || names.ready;
  /**
   * The review is finished: every part seen, the names accepted where there are any, and the
   * totals confirmed where there are any.
   */
  const reviewed =
    Boolean(review?.ok) && onLastPart && names.ready && (!needsConfirmation || totalsConfirmed);
  return {
    parts,
    part,
    names,
    canAdvance,
    partIndex: index,
    setPartIndex,
    onLastPart,
    totalsConfirmed,
    setTotalsConfirmed,
    reviewed,
    previous: () => setPartIndex((current) => Math.max(current - 1, 0)),
    next: () => setPartIndex((current) => Math.min(current + 1, parts.length - 1)),
  };
}

export type ReviewState = ReturnType<typeof useReviewParts>;

/** The drop area, for choosing a file. It gives way to the chosen file. */
export function FileDropArea({
  accept,
  hint,
  onChoose,
  inputRef,
}: {
  accept: string;
  hint: string;
  onChoose: (file: File) => void;
  inputRef: React.RefObject<HTMLInputElement>;
}) {
  const [dragging, setDragging] = useState(false);
  return (
    <div
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        const dropped = event.dataTransfer.files?.[0];
        if (dropped) onChoose(dropped);
      }}
      className={cn(
        'flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-10 text-center',
        'transition-[background-color,border-color,box-shadow] duration-150 ease-out',
        dragging
          ? 'cursor-copy border-primary bg-[color:var(--ts-row-hover)] shadow-raised'
          : 'border-[color:var(--ts-border-soft)] bg-blue-bglayout hover:border-[color:var(--ts-input-hover)]',
      )}
    >
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[color:var(--ts-primary-light)]">
        <UploadCloud className="h-6 w-6 text-primary" strokeWidth={1.75} aria-hidden="true" />
      </span>
      <p className="text-base text-gray-3">Drag your file here</p>
      <p className="text-base text-muted-foreground">{hint}</p>
      <FileInput accept={accept} onChoose={onChoose} inputRef={inputRef} />
      <Button className="mt-2" onClick={() => inputRef.current?.click()}>
        Choose a file
      </Button>
    </div>
  );
}

/** The hidden input both the drop area and the Change file button open. */
export function FileInput({
  accept,
  onChoose,
  inputRef,
}: {
  accept: string;
  onChoose: (file: File) => void;
  inputRef: React.RefObject<HTMLInputElement>;
}) {
  return (
    <input
      ref={inputRef}
      type="file"
      accept={accept}
      className="sr-only"
      onChange={(event) => {
        const chosen = event.target.files?.[0];
        // Cleared so that choosing the same file again still counts as a choice.
        event.target.value = '';
        if (chosen) onChoose(chosen);
      }}
    />
  );
}

/**
 * The chosen file, with Change file and Remove beside it. Change re-reads, re-maps and
 * re-totals the new file; Remove goes back to an empty drop area. Neither is offered once
 * bytes are moving.
 */
export function ChosenFile({
  file,
  tag,
  progress,
  busy,
  onChange,
  onRemove,
}: {
  file: File;
  tag?: string | null;
  progress: { percent: number; loaded?: number; total?: number } | null;
  busy: boolean;
  onChange: () => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-[color:var(--ts-border-soft)] bg-surface px-4 py-3 shadow-card">
      <div className="flex flex-wrap items-center gap-3">
        <FileSpreadsheet className="h-4 w-4 text-gray-1" aria-hidden="true" />
        <span className="text-base font-medium text-gray-3">{file.name}</span>
        <span className="text-base text-muted-foreground">{formatBytes(file.size)}</span>
        {tag ? <Pill tone="neutral">{tag}</Pill> : null}
        {progress ? (
          <span className="ml-auto text-base tabular-nums text-gray-3">
            {progress.percent < 100
              ? progress.loaded !== undefined && progress.total !== undefined
                ? `Uploading ${formatBytes(progress.loaded)} of ${formatBytes(progress.total)}`
                : `Uploading ${progress.percent}%`
              : 'Uploaded. Handing the file to Tab Service.'}
          </span>
        ) : (
          <span className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={busy} onClick={onChange}>
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Change file
            </Button>
            <Button variant="ghost" size="sm" disabled={busy} onClick={onRemove} aria-label="Remove this file">
              <X className="h-4 w-4" aria-hidden="true" />
              Remove
            </Button>
          </span>
        )}
      </div>
      {progress ? (
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress.percent}
          aria-label="Upload progress"
          className="flex items-center gap-3"
        >
          <span className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
            <span
              className="block h-full rounded-full bg-primary transition-[width] duration-150 ease-out"
              style={{ width: `${progress.percent}%` }}
            />
          </span>
          <span className="w-12 text-right text-base tabular-nums text-muted-foreground">{progress.percent}%</span>
        </div>
      ) : null}
    </div>
  );
}

/** What the check is doing right now, so the person can watch it happen. */
export function CheckingNote({ phase }: { phase: PrecheckPhase }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-[color:var(--ts-border-soft)] bg-[color:var(--ts-row-hover)] p-5">
      <Spinner />
      <div>
        <p className="text-base font-medium text-gray-3">{checkPhaseCopy[phase]}</p>
        <p className="text-base text-muted-foreground">This happens on your machine. Nothing is uploaded until it passes.</p>
      </div>
    </div>
  );
}

/** What the check actually looked at, in the person's terms. */
export function checkedSummary(result: PrecheckResult): string {
  const count = result.detected.recordCount;
  const records = count === undefined ? 'This file' : `${count.toLocaleString()} record${count === 1 ? '' : 's'}`;
  const form = result.detected.formType ? ` of ${result.detected.formType}` : '';
  return result.depth === 'full'
    ? `${records}${form} were read and checked against the IRS rules, and none of them would be refused.`
    : `${records}${form} were read, and the file has the columns this form needs.`;
}

/** A file that passed says so, and keeps saying so while it uploads. */
export function PassedNote({ result, uploading }: { result: PrecheckResult; uploading: boolean }) {
  return (
    <div className="rounded-lg border border-[color:var(--ts-pill-success-border)] bg-[color:var(--ts-pill-success-bg)]/40 p-5">
      <h3 className="text-base font-semibold text-[color:var(--ts-pill-success-text)]">
        {uploading ? 'Checked, and uploading' : 'Checked here. Ready to upload'}
      </h3>
      <p className="mt-1 text-base text-gray-3">
        {checkedSummary(result)}
        {result.depth === 'full'
          ? ' Tab Service checks it again on arrival, for the same reasons.'
          : ' The records themselves are checked on arrival.'}
      </p>
    </div>
  );
}

/**
 * What reading the file here found wrong. Shown instead of an upload, because none happened:
 * the file is still on their machine and nothing has been stored.
 */
export function PrecheckIssues({ result }: { result: PrecheckResult }) {
  return (
    <div className="rounded-lg border border-[color:var(--ts-pill-danger-border)] bg-[color:var(--ts-pill-danger-bg)]/40 p-5">
      <h3 className="text-base font-semibold text-[color:var(--ts-pill-danger-text)]">This file cannot be filed as it is</h3>
      <p className="mt-1 text-base text-gray-3">
        It was read here on your machine and nothing has been uploaded. Fix it and choose it again, or choose a
        different file.
      </p>
      <ul className="mt-4 flex flex-col gap-3">
        {result.issues.slice(0, 12).map((issue, index) => (
          <li key={`${issue.title}-${index}`} className="border-l-2 border-[color:var(--ts-pill-danger-border)] pl-4">
            <p className="text-base font-medium text-gray-3">
              {issue.title}
              {issue.where ? <span className="ml-2 font-mono text-sm text-muted-foreground">{issue.where}</span> : null}
            </p>
            <p className="text-base text-muted-foreground">{issue.detail}</p>
          </li>
        ))}
      </ul>
      {result.issues.length > 12 ? (
        <p className="mt-3 text-base text-muted-foreground">and {result.issues.length - 12} more.</p>
      ) : null}
    </div>
  );
}

/** The review: the part pills, and the current part underneath. */
export function ReviewParts({ file, review, state }: { file: File; review: PrecheckResult; state: ReviewState }) {
  const { parts, part, partIndex, setPartIndex, totalsConfirmed, setTotalsConfirmed } = state;
  if (parts.length === 0) return null;
  return (
    <div className="flex flex-col gap-5" data-testid="file-review">
      <ol className="flex flex-wrap items-center gap-2" aria-label="Review">
        {parts.map((name, index) => {
          const status = index < partIndex ? 'done' : index === partIndex ? 'current' : 'todo';
          return (
            <li key={name} className="flex items-center gap-2">
              {index > 0 ? <span className="h-px w-6 bg-[color:var(--ts-border-soft)]" aria-hidden="true" /> : null}
              <button
                type="button"
                onClick={() => setPartIndex(index)}
                aria-current={status === 'current' ? 'step' : undefined}
                className={cn(
                  'flex items-center gap-2 rounded-full border px-3 py-1.5 text-base transition-colors duration-150 ease-out',
                  status === 'current'
                    ? 'border-primary bg-[color:var(--ts-primary-light)] font-medium text-primary'
                    : status === 'done'
                      ? 'border-[color:var(--ts-pill-success-border)] text-gray-3 hover:bg-[color:var(--ts-row-hover)]'
                      : 'border-[color:var(--ts-border-soft)] text-muted-foreground hover:bg-[color:var(--ts-row-hover)]',
                )}
              >
                <span
                  className={cn(
                    'flex h-5 w-5 items-center justify-center rounded-full text-xs font-semibold',
                    status === 'current'
                      ? 'bg-primary text-white'
                      : status === 'done'
                        ? 'bg-[color:var(--ts-pill-success-bg)] text-[color:var(--ts-pill-success-text)]'
                        : 'bg-muted text-muted-foreground',
                  )}
                >
                  {status === 'done' ? <Check className="h-3 w-3" aria-hidden="true" /> : index + 1}
                </span>
                {reviewPartCopy[name].label}
                {name === 'issues' ? <Pill tone="danger">{formatCount(review.issues.length)}</Pill> : null}
              </button>
            </li>
          );
        })}
      </ol>

      {part === 'mapping' && review.mapping ? (
        <div className="flex flex-col gap-5">
          <div>
            <h3 className="text-base font-semibold text-gray-3">{reviewPartCopy.mapping.label}</h3>
            <p className="text-base text-muted-foreground">
              How each column in {file.name} will be read. Unmatched columns are ignored; rename them in the file if
              they should be filed.
            </p>
          </div>
          <ColumnMapping mapping={review.mapping} />
          {review.unmapped?.length ? (
            <div>
              <p className="mb-2 text-base font-medium text-gray-3">Ignored columns</p>
              <div className="flex flex-wrap gap-2">
                {review.unmapped.map((column) => (
                  <span key={column} className="rounded-md bg-muted px-2 py-1 text-sm text-gray-3">
                    {column}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {part === 'names' ? <ResolveNames names={state.names} /> : null}

      {part === 'issues' ? <PrecheckIssues result={review} /> : null}

      {part === 'totals' ? (
        <div className="flex flex-col gap-5">
          <div>
            <h3 className="text-base font-semibold text-gray-3">{reviewPartCopy.totals.label}</h3>
            <p className="text-base text-muted-foreground">
              What the {formatCount(review.detected.recordCount ?? 0)} records add up to, box by box, read here on
              your machine. Check them against your own totals.
            </p>
          </div>
          <BoxTotals
            boxes={review.boxTotals}
            unmappedColumns={review.unmapped ?? []}
            recordCount={review.detected.recordCount ?? 0}
            bare
          />
          {review.ok ? (
            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-[color:var(--ts-border-soft)] bg-blue-bglayout px-4 py-3">
              <Checkbox
                checked={totalsConfirmed}
                label="These totals match my records"
                onCheckedChange={(next) => setTotalsConfirmed(next === true)}
              />
              <span>
                <span className="block text-base font-medium text-gray-3">These totals match my records</span>
                <span className="block text-sm text-muted-foreground">
                  Upload is offered once you confirm. Tab Service checks the file again on arrival.
                </span>
              </span>
            </label>
          ) : (
            <p className="text-base text-muted-foreground">
              The file cannot be uploaded until the issues are fixed. Fix it and choose it again.
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The footer of a review: previous part or Back on the left; next part, or the upload
 * itself on the last part, on the right.
 */
export function ReviewFooter({
  state,
  transferring,
  canUpload,
  busy,
  phase,
  uploadLabel,
  backLabel = 'Back',
  onBack,
  onUpload,
}: {
  state: ReviewState;
  transferring: boolean;
  canUpload: boolean;
  busy: boolean;
  phase: PrecheckPhase | null;
  uploadLabel: string;
  backLabel?: string;
  onBack?: () => void;
  onUpload: () => void;
}) {
  const { parts, part, partIndex, onLastPart, previous, next, canAdvance, names } = state;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 pt-6">
      {parts.length > 0 && partIndex > 0 && !transferring ? (
        <Button variant="ghost" onClick={previous}>
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {reviewPartCopy[parts[partIndex - 1] as ReviewPart].label}
        </Button>
      ) : onBack ? (
        <Button variant="ghost" onClick={onBack} disabled={busy}>
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {backLabel}
        </Button>
      ) : (
        <span />
      )}
      {part === 'names' && !names.consented && names.decisions.length > 0 && !transferring ? (
        <span className="ml-auto text-xs text-muted-foreground">Accept the identifications above to continue.</span>
      ) : null}
      {parts.length > 0 && !onLastPart && !transferring ? (
        <Button variant="primary" onClick={next} disabled={!canAdvance}>
          {part === 'names' && names.isClassifying
            ? 'Identifying…'
            : reviewPartCopy[parts[partIndex + 1] as ReviewPart].label}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Button>
      ) : (
        <Button variant="primary" disabled={!canUpload} onClick={onUpload} data-testid="upload-after-review">
          {busy || phase ? <Spinner /> : <UploadCloud className="h-4 w-4" aria-hidden="true" />}
          {phase ? checkPhaseCopy[phase] : uploadLabel}
        </Button>
      )}
    </div>
  );
}
